import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg, NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { NodeSpec, RequiredSockets } from './NodeSpec';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const pickLightChannelUiNodeSpec: NodeSpec<NodeCfg<'pickLightChannel'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'Pick Light',
    requiredSockets: (_node, _availableInputSockets, availableOutputSockets) => ({
        required: [ 'channelNumber' ],
        conditionallyRequired: [ {
            kind: 'or',
            availableOptions: availableOutputSockets,
        } ],
    } satisfies ReturnType<RequiredSockets<'pickLightChannel', NodeSocketKindMap['pickLightChannel']>>),
    kindLabel: 'Pick Light Channel',
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.Ptl110PickLight ],
            },
            on: { 'update:value': defaultControlUpdateValueHandler },
            value: nodeCfg.controlValues.connectionId,
        }),
    }),
    cfg: () => ({ id: getUID(), kind: 'pickLightChannel', width: 220, controlValues: { connectionId: '' } }),
    inputs: nodeCfg => ({
        setOrientation: { socket: 'event', label: 'Set Orientation' },
        resetLights: { socket: 'event', label: 'Reset Lights' },
        toggleTestLights: { socket: 'event', label: 'Toggle Light Test Mode' },
        toggleAddressingMode: { socket: 'event', label: 'Toggle Addressing Mode' },
        channelNumber: {
            socket: 'number',
            label: 'Channel Number',
            controlOpts: defineControlOpts({
                cfg: { kind: 'number', min: 0, max: 4 },
                on: { 'update:value': defaultControlUpdateValueHandler },
                value: nodeCfg.socketDefaults?.inputs?.channelNumber,
            }),
        },
        addressingModeStartingId: {
            socket: 'number',
            label: 'Addressing Mode Starting Id',
            controlOpts: defineControlOpts({
                cfg: { kind: 'number', min: 1, max: 190 },
                on: { 'update:value': defaultControlUpdateValueHandler },
                value: nodeCfg.socketDefaults?.inputs?.addressingModeStartingId,
            }),
        },
        addressingModeEndingId: {
            socket: 'number',
            label: 'Addressing Mode Ending Id',
            controlOpts: defineControlOpts({
                cfg: { kind: 'number', min: 1, max: 190 },
                on: { 'update:value': defaultControlUpdateValueHandler },
                value: nodeCfg.socketDefaults?.inputs?.addressingModeEndingId,
            }),
        },
        orientation: {
            socket: 'string',
            label: 'Display Orientation',
            controlOpts: defineControlOpts({
                cfg: {
                    kind: 'selection',
                    items: [
                        { label: 'Standard', value: 'standard' },
                        { label: 'Inverted', value: 'inverted' },
                    ],
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
                value: nodeCfg.socketDefaults?.inputs?.orientation,
            }),
        },
    }),
    outputs: () => ({
        picked: { socket: 'event', label: 'Light Picked' },
        pickedLightId: { socket: 'number', label: 'Picked Light Id' },
        pickedLightChannelNum: { socket: 'number', label: 'Picked Light Channel Number' },
    }),
    validator: node => node.controlValues.connectionId
        ? true
        : { connectionId: 'Connection id is required' },
};
